import React, {useState} from 'react';
import {Box, Typography} from '@mui/material';
import ArrDataGrid from '../../common/arr-data-grid/ArrDataGrid';
import ArrButton from '../../common/arr-button/ArrButton';
import {ReactComponent as NoDataIcon} from '../../../assets/images/no-data-found.svg';
import {t} from 'i18next';
import './DashboardAssignmentGrid.scss';
import {Assignment} from '../dashboardTypes';
import ArrLinearProgressBar from '../../common/arr-linear-progressbar/ArrLinearProgressBar';
import {
  ASSIGNMENT_PROGRESS_DECIMALS,
  NO_DATA_PLACEHOLDER,
  PERCENTAGE,
  RoleId,
  SourceTypeCode,
} from '../../../shared/constants';

import {ReactComponent as InfoIcon} from '../../../assets/images/info-icon.svg';

import {ReactComponent as SpeedoMeter} from '../../../assets/images/speedo-meter-icon.svg';
import {GridColDef} from '@mui/x-data-grid';
import ArrIconButton from '../../common/arr-icon-button/ArrIconButton';
import ArrModal from '../../common/arr-modal/ArrModal';
import {useDocumentViewerListId} from '../../../hooks/document/useDocument';
import {clearDocViewState} from '../../document-viewer/docViewerUtils';
import {
  storeAssignmentSource,
  storeDocumentListMasterId,
  storeSourceId,
  storeSourceTypeCode,
} from '../../../redux/slice/docViewSlice';
import {useDispatch, useSelector} from 'react-redux';
import {useNavigate} from 'react-router-dom';
import {createDocumentViewerListIdRequestPayload} from '../../document-viewer/docViewerUtils';
import ArrLoadingSpinner from '../../common/arr-loading-spinner/ArrLoadingSpinner';
import {RootState} from '../../../redux/store';
// 0% and 100% are exact states, so a decimal place there implies a precision
// that does not exist. Every value in between keeps one decimal.
export const formatAssignmentProgress = (value: unknown) => {
  if (typeof value !== 'number') {
    return NO_DATA_PLACEHOLDER;
  }
  const decimals =
    value === 0 || value === 100 ? 0 : ASSIGNMENT_PROGRESS_DECIMALS;
  return `${value.toFixed(decimals)}${PERCENTAGE}`;
};

interface IDashboardAssignmentGrid {
  assignments: Assignment[];
  loading?: boolean;
  onDrawClick: (assignmentId: number) => void;
  className?: string;
}

const DashboardAssignmentGrid: React.FC<IDashboardAssignmentGrid> = ({
  assignments,
  className,
  onDrawClick,
  loading = false,
}) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [isDocViewClickLoading, setIsDocViewClickLoading] = useState(false);
  const [assignmentInstruction, setAssignmentInstruction] = useState('');
  const {mutate: fetchDocumentViewerListId} = useDocumentViewerListId();
  const roleId = useSelector(
    (state: RootState) => state.authenticatedUser.user.roleId
  );
  const handleViewInDocView = (assignmentID: number) => {
    const userRole = String(roleId);
    if (userRole != RoleId.ReviewManager && userRole != RoleId.Reviewer) {
      return;
    }
    const requestPayload = createDocumentViewerListIdRequestPayload({
      sourceId: assignmentID,
      sourceTypeCode: SourceTypeCode.ASSIGNMENT,
      docViewSourceTypeCode: SourceTypeCode.ASSIGNMENT,

      sourceDetailOverrides: {},
      docsToExcludeOverrides: {},
      sourceInfoOverrides: {
        sourceTypeCode: SourceTypeCode.ASSIGNMENT,
        assignmentSource: SourceTypeCode.REVIEWERDASHBOARD,
        isMyAssignment: true,
        isInverse: true,
        isDefaultLoad: true,
        isAllChildrenSelected: true,
        destinationContextTypeCode: '',
        isDocumentCount: false,
      },
      docViewSessionOverrides: {
        assignmentSortDetails: [
          {
            sourceID: 0,
            columnManual: '',
            columnOptimized: '',
            columnIDsOptimized: '',
            columnOrderOptimized: '',
            columnIDsManual: '',
            columnOrderManual: '',
            showCompletedOptimized: true,
            showCompletedManual: true,
            columnDisplayManual: '',
            columnDataTypeOptimized: [],
            columnDataTypeManual: [],
          },
        ],
        docsForCodeSame: [],
      },
      sortConfigurationOverrides: {
        manualCols: '',
        showCompletedDocOptimized: true,
        showCompletedDocManual: true,
        configuredShowCompletedDoc: true,
      },
    });
    setIsDocViewClickLoading(true);
    fetchDocumentViewerListId(requestPayload, {
      onSuccess: (response) => {
        clearDocViewState();
        const masterId =
          response.documentListID ?? response.defaultMasterID ?? 0;
        setTimeout(() => {
          setIsDocViewClickLoading(false);
          dispatch(storeDocumentListMasterId(String(masterId)));
          dispatch(storeSourceTypeCode(SourceTypeCode.ASSIGNMENT));
          dispatch(storeAssignmentSource(SourceTypeCode.REVIEWERDASHBOARD));
          dispatch(storeSourceId(assignmentID));
          navigate('/DocumentViewer/DocView');
        }, 3000);
      },
    });
  };

  const dashboardAssignmentsColumn: GridColDef[] = [
    {
      field: 'priority',
      headerName: t('dashboard.reviewer.priority'),
      width: 86,
      renderCell: (params) => (
        <Box
          className="priority"
          data-testid={`${params.row?.assignmentID}-priority`}
        >
          {params.row.priority ?? NO_DATA_PLACEHOLDER}
        </Box>
      ),
    },
    {
      field: 'velocityScore',
      headerName: t('dashboard.reviewer.velocity'),
      renderHeader: () => {
        return (
          <Box className="velocity-wrapper">
            <Typography
              className="typo-text label2_size  wrap-text"
              data-testid={`dashboard-assignment-grid-velocity`}>
              {t('dashboard.reviewer.velocity')}
            </Typography>
          </Box>
        );
      },
      renderCell: (params) => (
        <Box
          className="velocity"
          data-testid={`${params.row?.assignmentID}-velocity`}
        >
          {params.row.velocityScore ? (
            <>
              <SpeedoMeter />
              {params.row.velocityScore}
            </>
          ) : (
            NO_DATA_PLACEHOLDER
          )}
        </Box>
      ),
      width: 220,
    },
    {
      field: 'assignment',
      headerName: t('dashboard.reviewer.myAssignments'),
      flex: 1,
      renderCell: (params) => {
        const {
          assignmentName,
          assignmentLevel,
          assignmentFullPath,
          assignmentInstructions,
          assignmentID,
        } = params.row;

        return (
          <Box className="full-box">
            <Box className="assignment-level">
              {assignmentName ? (
                <>
                  <Typography
                    className="typo-text subhead5_size assignment-name-text"
                    data-testid={`${assignmentID}-name`}
                    onClick={() => {
                      handleViewInDocView(assignmentID);
                    }}
                  >
                    {assignmentName}
                  </Typography>
                  <Box className="assignment-name-subtext">
                    <Typography
                      className="typo-text label3_size project-name-text"
                      data-testid={`${assignmentID}-assignment-details`}
                    >
                      {assignmentLevel}, {assignmentFullPath}
                    </Typography>
                    {assignmentInstructions && (
                      <ArrIconButton
                        size="small"
                        onClick={() =>
                          setAssignmentInstruction(assignmentInstructions)
                        }
                        className="info-assignment-button"
                        ariaLabel={t('common.arrWidgetWrapper.info')}
                        testId={`${assignmentName}-instructions`}
                        data-testid={`${assignmentID}-view-instructions`}
                        icon={
                          <InfoIcon
                            fontSize="small"
                            className="assignment-icon"
                          />
                        }
                        shape="none"
                      />
                    )}
                  </Box>
                </>
              ) : (
                NO_DATA_PLACEHOLDER
              )}
            </Box>
          </Box>
        );
      },
    },
    {
      field: 'assignmentProgress',
      headerName: t('dashboard.reviewer.overallAssignment'),
      flex: 0.75,
      valueGetter: (_value, row) =>
        row?.assignmentProgressPrecise ?? row?.assignmentProgress,
      renderCell: (params) => (
        <Box data-testid={`${params.row?.assignmentID}-assignment-progress`}>
          {formatAssignmentProgress(params.value)}
        </Box>
      ),
    },
    {
      field: 'batchProgress',
      headerName: t('dashboard.reviewer.progress'),
      flex: 1,
      renderCell: (params) => (
        <Box className="full-box">
          <Box className="progress-box">
            <Box className="progress-bar">
              <ArrLinearProgressBar
                completed={params.row.batchProgressCompleted}
                total={params.row.batchTotalDocs}
                id={params.row?.assignmentID}
              />
            </Box>
            {params.row.canUserDrawFromPool ? (
              <ArrButton
                variant="secondary"
                size="xs"
                aria-label={`draw`}
                dataTestId={`dashboard-draw-${params.row.assignmentID}`}
                onClick={() => onDrawClick(params.row?.assignmentID)}
              >
                {t('dashboard.reviewer.draw')}
              </ArrButton>
            ) : null}
          </Box>
        </Box>
      ),
    },
  ];
  const AssignmentHeader = () => {
    return (
      <Box className="assignment-header">
        <Typography
          className="typo-text subhead4_size header_text"
          data-testid={`dashboard-assignment-group-header`}
        >
          {t('dashboard.reviewer.assignmentGroup')}
        </Typography>
      </Box>
    );
  };

  const NoRowsOverlay = () => (
    <Box className="no-project-data-found">
      <NoDataIcon />
      <Typography
        className="typo-text headline3_size no-project-text"
        data-testid={`empty-reviewer-text`}
      >
        {t('dashboard.reviewer.emptyData')}
      </Typography>
    </Box>
  );

  return (
    <Box className="assignment-container">
      <AssignmentHeader />
      <ArrDataGrid
        rows={assignments}
        columns={dashboardAssignmentsColumn}
        primaryKey="assignmentID"
        currentPage={1}
        noRowsOverlay={NoRowsOverlay}
        className={className ?? 'domain-admin-dashboard-grid'}
        loading={loading}
      />
      <ArrModal
        showModal={!!assignmentInstruction}
        onClose={() => {
          setAssignmentInstruction('');
        }}
        headerTitle={t('dashboard.reviewer.instructions')}
        dataTestId="close-instruction-modal"
        buttons={[
          {
            id: '1',
            label: 'Close',
            onClick: () => setAssignmentInstruction(''),
            variant: 'primary',
            dataTestId: 'close-instruction-modal-btn',
          },
        ]}
      >
        <Typography
          component="div"
          className="typo-text body2_size desc-text-color"
          color="white"
          data-testid={`assignment-instructions`}
          dangerouslySetInnerHTML={{__html: assignmentInstruction}}
        />
      </ArrModal>
      {isDocViewClickLoading && (
        <ArrLoadingSpinner overlay isOpen={isDocViewClickLoading} />
      )}
    </Box>
  );
};

export default DashboardAssignmentGrid;
